// The customer's memberships as the profile shows them: one entry per membership, not one per row
// and not one per purchase.
//
// A continuous membership adds a new assignment row (users/{uid}/memberships/active/{cid}/{id}) for
// every renewed period - each with its own payment and receipt - and moves the status fields
// (chargestate, nextcharge, ...) onto the newest row. Listed row by row that reads as several
// unrelated memberships. Here consecutive periods of one membership become one chain.
//
// A row joins a chain when it carries the chain's subscription.id, or when it starts the day the
// chain ends and does not carry a different subscription id. Dates are needed because the first
// period loses its subscription fields when they move to the renewal.

const dateOf = (value) => (value ? String(value).slice(0, 10) : '')

const subscriptionIdOf = (row) => (row && row.subscription && row.subscription.id) || null

// today: 'YYYY-MM-DD'
export const groupMemberships = (assignments, today) => {
  const rows = (Array.isArray(assignments) ? assignments : []).filter((r) => r && r.membershipid)
  const entries = []

  rows
    .filter((r) => r.permanent)
    .forEach((r) => {
      entries.push({
        key: 'p:' + r.id,
        membershipid: r.membershipid,
        permanent: true,
        start: dateOf(r.start),
        end: null,
        rows: [r],
        stateRows: r.chargestate ? [r] : [],
        stateRow: r.chargestate ? r : null,
      })
    })

  const chains = []
  rows
    .filter((r) => !r.permanent)
    .slice()
    .sort((a, b) => dateOf(a.start).localeCompare(dateOf(b.start)) || dateOf(a.end).localeCompare(dateOf(b.end)))
    .forEach((r) => {
      const sid = subscriptionIdOf(r)
      const chain = chains.find((c) => {
        if (c.membershipid !== r.membershipid) return false
        if (sid && c.sids.has(sid)) return true
        const otherSubscription = sid && c.sids.size > 0 && !c.sids.has(sid)
        return !otherSubscription && dateOf(r.start) === c.end
      })
      if (chain) {
        chain.rows.push(r)
        chain.end = dateOf(r.end)
        if (sid) chain.sids.add(sid)
      } else {
        chains.push({ membershipid: r.membershipid, rows: [r], end: dateOf(r.end), sids: new Set(sid ? [sid] : []) })
      }
    })

  // Then every chain of one membership becomes ONE entry: the customer has one membership, however
  // many times it was bought. A subscription that failed and was bought again is the same
  // membership; showing them apart left "Osta uudelleen" on the old one next to the new one.
  const byMembership = {}
  chains.forEach((c) => {
    if (!byMembership[c.membershipid]) byMembership[c.membershipid] = []
    byMembership[c.membershipid].push(c)
  })
  Object.keys(byMembership).forEach((membershipid) => {
    const list = byMembership[membershipid]
    const rowsNewestFirst = list
      .flatMap((c) => c.rows)
      .sort((a, b) => dateOf(b.start).localeCompare(dateOf(a.start)) || dateOf(b.end).localeCompare(dateOf(a.end)))
    // Each chain's state is on its newest row that has one.
    const chainStates = list
      .map((c) => ({ end: c.end, row: c.rows.slice().reverse().find((r) => r.chargestate) || null }))
      .filter((s) => s.row)
    // Every active subscription shows (each needs its own "Peru tilaus"); with none active, the
    // most recent subscription's state - e.g. failed, with "Osta uudelleen".
    const active = chainStates.filter((s) => s.row.chargestate === 'active').map((s) => s.row)
    const latest = chainStates.slice().sort((a, b) => String(b.end).localeCompare(String(a.end)))[0]
    const stateRows = active.length > 0 ? active : latest ? [latest.row] : []
    entries.push({
      key: 'm:' + membershipid,
      membershipid,
      permanent: false,
      start: list.map((c) => dateOf(c.rows[0].start)).sort()[0],
      end: list.map((c) => c.end).sort().slice(-1)[0],
      rows: rowsNewestFirst,
      chains: list.length,
      stateRows,
      stateRow: stateRows[0] || null,
    })
  })

  entries.forEach((e) => {
    e.ended = !e.permanent && !!e.end && e.end < today
    e.upcoming = !!e.start && e.start > today
  })

  // Current and upcoming first, the soonest-ending on top; permanent after them; ended last, newest first.
  const rank = (e) => (e.ended ? 2 : e.permanent ? 1 : 0)
  return entries.sort((a, b) => {
    if (rank(a) !== rank(b)) return rank(a) - rank(b)
    if (rank(a) === 2) return String(b.end).localeCompare(String(a.end))
    if (rank(a) === 1) return String(a.start).localeCompare(String(b.start))
    return String(a.end).localeCompare(String(b.end))
  })
}

// '60,00 €' from a row's payment (cents), '' when the row was not paid online (granted by the venue).
export const periodAmount = (row) => {
  const amount = row && row.payment && Number(row.payment.amount)
  return amount || amount === 0 ? (amount / 100).toFixed(2).replace('.', ',') + ' €' : ''
}
